import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiPlaneLine, RiTeamLine, RiCheckboxCircleLine, RiTimeLine, RiFileList3Line, RiCalendarLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiBuildingLine, RiFileAddLine, RiAlertLine, RiInformationFill, RiAlertFill, RiBankCardLine, RiBankLine, RiInformationLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig, getEffectiveQuoteStatus, selectAcceptedBidOption } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';
import { getEffectiveAircraft } from '@/features/tails/utils';
/**
 * QuoteCard Component
 * Displays a single quote with client info, flight details, and actions
 *
 * @param {Object} props
 * @param {Object} props.quote - Quote data object
 * @param {Function} props.onViewQuote - Callback when "View Quote" is clicked
 */
const QuoteCard = ({ quote, onViewQuote, onViewInvoice, hideActions = false }) => {
  const navigate = useNavigate();

  // "Build Trip Sheet" requires create + update on Booking & Quoting
  // (admin, broker, junior_broker — read-only roles excluded).
  // flight_coordinator is included because trip sheets are operations work
  // they own via the Tracking module.
  const { can, role } = usePermissions();
  const canBuildTripSheet =
    (can(MODULES.BOOKING, ACTIONS.CREATE) && can(MODULES.BOOKING, ACTIONS.UPDATE)) ||
    normalizeRole(role) === ROLES.FLIGHT_COORDINATOR;

  // Resolve data from either bid_items (quotes endpoint) or bids (client endpoint)
  const firstBid = quote.bid_items?.[0]?.bid || quote.bids?.[0] || {};
  const legs = firstBid.leg_itinerary || [];
  // Aggregate options across ALL bids on the quote, not just the first
  // bid — multi-bid trip groups (multi-leg trips with different tails
  // per leg) would otherwise undercount. Drop soft-deleted / rejected
  // rows (shortlisted:false — new REJECTED writes flip both fields in
  // lockstep, legacy rows only have the shortlisted flag) so the "N
  // tail options" count matches what the client sees in the quote
  // preview and itinerary email.
  const bidOptions = (
    (quote.bid_items || []).flatMap((bi) => bi?.bid?.bid_options || bi?.bid_options || [])
    // Fallback for the alt shape returned by the client `/my-trips` endpoint
    // (quote.bids[] instead of quote.bid_items[]).
    .concat(!quote.bid_items ? (quote.bids || []).flatMap((b) => b?.bid_options || []) : [])
  ).filter((o) => {
    // Guard on BOTH signals so a slimmed response that omits one still
    // filters correctly. `/quotes` list rows come from
    // `apiClient.getFinancialReports` — a unified endpoint that may
    // return a lighter bid_option shape than `getQuoteById`. Under
    // lockstep (SENT/DRAFT/ACCEPTED ⇔ shortlisted:true, REJECTED ⇔
    // shortlisted:false) both fields agree, so this OR is a no-op in
    // the healthy case; it saves us when only one field survives.
    if (!o) return false;
    if (o.shortlisted === false) return false;
    if (o.status === 'REJECTED') return false;
    return true;
  });

  // Once the client picks an aircraft option at checkout, that option flips
  // to status=ACCEPTED (see CardInfoForm.payQuote). It's the only status
  // write that path performs, and siblings stay untouched — so exactly one
  // ACCEPTED means "the client picked this one and paid," and the card
  // should collapse from "N tail options" to just the chosen tail.
  const acceptedOption = selectAcceptedBidOption(bidOptions);
  const acceptedAircraft = acceptedOption ? getEffectiveAircraft(acceptedOption.aircraft) : null;
  const acceptedTailLabel = acceptedAircraft?.tail_number
    || acceptedAircraft?.name
    || acceptedAircraft?.make_model
    || 'Booked aircraft';

  const excludeStatus = ["DRAFT", "SENT", "VIEWED", "SIGNED", "EXPIRED", "DECLINED", "CANCELLED"];
  const quoteStatus = quote?.status === "PAID" ? "ACCEPTED" : quote?.status;
  // Promote the raw payment_status into the badge-friendly value the card
  // renders. Rules (evaluated top-down):
  //   CANCELLED                     → REJECTED  (regardless of stage)
  //   PRE_AUTHORIZED + active stage → PAYMENT_PROCESSING
  //   WIRE_TRANSFER_IN_PROGRESS +
  //     active stage                → CAPTURED  (wire has cleared enough to accept)
  //   otherwise                     → raw payment_status
  const rawPaymentStatus = quote?.payment_status;
  const isActiveStage = !excludeStatus.includes(quoteStatus);
  const paymentStatus =
    rawPaymentStatus === "CANCELLED" ? "REJECTED" :
    isActiveStage && (rawPaymentStatus === "PRE_AUTHORIZED" || rawPaymentStatus === "WIRE_TRANSFER_IN_PROGRESS") ? "PAYMENT_PROCESSING" :
    rawPaymentStatus;

  // Route through the promotion helper so PASSENGERS_PENDING reads as
  // "Trip Sheet In Progress" once the client has submitted passenger
  // details — matches what QuoteDetailsDrawer already does. Without
  // this, the card and drawer disagree on the badge for the same quote.
  const configStatus = getQuoteStatusConfig(getEffectiveQuoteStatus(quote, quoteStatus));
  const Icon = configStatus.icon;

  const statusUpper = quoteStatus.toUpperCase();
  const needsPaidAction =
    (statusUpper === 'SENT' || statusUpper === 'VIEWED' || statusUpper === 'SIGNED') &&
    (paymentStatus || '').toUpperCase() === 'CAPTURED';

  const handleViewClick = () => {
    if (onViewQuote) {
      onViewQuote(quote, quoteStatus, paymentStatus);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };

  // PASSENGERS_PENDING and WAITING_FLIGHT only ever exist AFTER payment
  // succeeds server-side, so the button shows unconditionally for those.
  // ACCEPTED (which quoteStatus also aliases from raw PAID) can technically
  // reach the FE without payment on data drift, so we still gate it on
  // the resolved paymentStatus being one of the in-flight / captured
  // states. CAPTURED is included so a fully-paid PASSENGERS_PENDING/
  // ACCEPTED quote doesn't hide the button.
  const showBuildTripSheet =
    (quoteStatus === 'PASSENGERS_PENDING'
    || quoteStatus === 'WAITING_FLIGHT'
    || quoteStatus === 'ACCEPTED')
        && (paymentStatus === 'WIRE_TRANSFER_IN_PROGRESS'
            || paymentStatus === 'PAYMENT_PROCESSING'
            || paymentStatus === 'ACCEPTED'
            || paymentStatus === 'CAPTURED'
            || paymentStatus === 'SETTLED_VIA_PASSPORT');

  const showPreauthWarning = excludeStatus.includes(quoteStatus) && (paymentStatus && (paymentStatus=="PRE_AUTHORIZED" || paymentStatus=="WIRE_TRANSFER_IN_PROGRESS"));

  // Pre-auth holds are valid for 7 days from the invoice creation (Visa/MC
  // standard). Past that window the funds are released automatically and
  // re-authorization is required. Computed once so both the warning row
  // and the expiry sub-line can use it.
  const holdAnchor = quote?.invoice?.created_at || quote?.created_at;
  const holdExpiresAt = holdAnchor
    ? new Date(new Date(holdAnchor).getTime() + 7 * 24 * 60 * 60 * 1000)
    : null;
  const holdMsLeft = holdExpiresAt ? holdExpiresAt.getTime() - Date.now() : null;
  const holdDaysLeft = holdMsLeft != null ? Math.max(0, Math.ceil(holdMsLeft / (24 * 60 * 60 * 1000))) : null;
  const isHoldExpired = holdMsLeft != null && holdMsLeft <= 0;

  // Where the money currently sits, keyed off payment_status. Purely UI
  // copy — no schema field. Only rendered when we have a meaningful post-
  // capture state to describe.
  const paymentLocationLabel =
    paymentStatus === 'CAPTURED' ? 'At Payment Merchant' :
    paymentStatus === 'SETTLED_VIA_PASSPORT' ? 'Settled to Bank' :
    null;

  const passengerCount = quote.passenger_count || 0;
  const showUnpaidInvoiceWarning = quote.invoice_id && quoteStatus !== 'ACCEPTED';
  const showAdvanceInvoiceNote = quote.invoice_id && quote.invoice_type === 'ADVANCE';

  return (
    <Card className="@container group hover:shadow-lg transition-all duration-200 overflow-hidden bg-white !rounded-3xl">
      <CardContent className="p-4 @lg:p-5 grid grid-cols-1 @lg:grid-cols-[180px_1fr] gap-4 @lg:gap-6">
        {/* Left column: client info → status → warnings → info stack */}
        <div className="min-w-0 flex flex-col gap-3">
          {/* Client info */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="font-semibold text-md text-gray-900 truncate">
                {quote.client?.first_name} {quote.client?.last_name}
              </h3>
              {quote.client?.isVerified && (
                <RiCheckboxCircleLine className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              )}
            </div>
            <div className="text-sm text-gray-700 flex items-center gap-1.5 truncate">
              <RiBuildingLine className="w-3.5 h-3.5 shrink-0 text-gray-500" />
              <span className="truncate">{quote.client?.company_name || quote.client?.company || '-'}</span>
            </div>
            <div className="text-sm text-gray-700 flex items-center gap-1.5 truncate">
              <RiFileList3Line className="w-3.5 h-3.5 shrink-0 text-gray-500" />
              <span className="font-medium truncate">{quote.quote_number || '—'}</span>
            </div>
          </div>

          {/* Status badge + Mark as Paid pill */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-md shrink-0 ${configStatus.className}`}>
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {configStatus.label}
            </span>
            {needsPaidAction && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-200 whitespace-nowrap shrink-0"
                title="Payment captured but quote is not yet marked as Paid"
              >
                <RiAlertLine className="w-3 h-3" />
                Mark as Paid
              </span>
            )}
          </div>

          {/* Info stack */}
          <div className="text-sm text-gray-700">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <RiTeamLine className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <span className="truncate">{passengerCount} Passenger{passengerCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <RiPlaneLine className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                {acceptedOption
                  ? <span className="truncate">{acceptedTailLabel}</span>
                  : <span className="truncate">{bidOptions.length} tail options</span>}
              </div>
            </div>

            {(paymentStatus || paymentLocationLabel || showAdvanceInvoiceNote) && (
              <div className="mt-2 pt-2 space-y-1.5">
                {paymentStatus && (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <RiBankCardLine className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="truncate">{getQuoteStatusConfig(paymentStatus).label}</span>
                  </div>
                )}
                {paymentLocationLabel && (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <RiBankLine className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="truncate">{paymentLocationLabel}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right column: flight legs (top) → warnings → actions (bottom, right-aligned) */}
        <div className="@container min-w-0 flex flex-col gap-3">
          <div className="bg-[#f7f7f7] rounded-md px-3 py-2.5 space-y-1.5">
            {legs.map((leg, i) => (
              <div key={`ml-${i}`} className="flex flex-col @xs:flex-row @xs:items-center @xs:justify-between gap-1 @xs:gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-900 font-medium min-w-0">
                  <RiFlightTakeoffLine className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                  <span>{leg.departure_airport}</span>
                  <RiArrowRightLine className="w-3 h-3 text-gray-600 mx-1 @xs:mx-2 shrink-0" />
                  <RiFlightLandLine className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                  <span>{leg.arrival_airport}</span>
                </div>
                <div className="flex items-center gap-3 @xs:gap-5 text-gray-500 shrink-0">
                  <div className="flex items-center gap-1">
                    <RiCalendarLine className="w-3 h-3" />
                    <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), 'D MMM')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <RiTimeLine className="w-3 h-3" />
                    <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), 'HH:mm')}</span>
                  </div>
                </div>
              </div>
            ))}

            {showAdvanceInvoiceNote && (
              <div className="flex items-center gap-1.5 text-sm text-gray-400">
                <RiInformationFill className="w-3.5 h-3.5 shrink-0" />
                <span>Invoice not linked to a trip</span>
              </div>
            )}
          </div>

          {/* Warnings — sit below the flight legs when active */}
          {showPreauthWarning && (
            <div className="space-y-2">
              <div className="flex flex-col @sm:flex-row @sm:items-center @sm:justify-between gap-1.5 @sm:gap-2 px-3 py-2 bg-amberlight text-sm rounded-md">
                <div className="flex items-center gap-1.5 min-w-0">
                  <RiAlertFill className="w-3.5 h-3.5 shrink-0 text-amberdark" />
                  <span className="text-gray-600">Action needed for confirming trip and payment.</span>
                </div>
                <button
                  type="button"
                  onClick={handleViewClick}
                  className="font-medium underline text-black shrink-0 cursor-pointer hover:text-primary-700 transition-colors self-start @sm:self-auto"
                >
                  Proceed
                </button>
              </div>
            </div>
          )}
          {showUnpaidInvoiceWarning && !showPreauthWarning && (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-amberlight text-sm rounded-md">
              <RiAlertFill className="w-3.5 h-3.5 shrink-0 text-amberdark" />
              <span className="text-gray-600 truncate">Unpaid {quote.invoice_type?.toLowerCase()} invoice</span>
            </div>
          )}

          {!hideActions && (
            <div className="flex flex-col @sm:flex-row gap-2 mt-auto">
              {!quote.invoice_id ? (
                <button
                  onClick={handleViewClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 @sm:px-5 py-2.5 @sm:py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors"
                >
                  <RiInformationLine className="w-4 h-4" />
                  <span>Quote Details</span>
                </button>
              ) : (
                <button
                  onClick={() => onViewInvoice?.(quote, quoteStatus, paymentStatus)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 @sm:px-5 py-2.5 @sm:py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors"
                >
                  <RiFileList3Line className="w-4 h-4" />
                  <span>View Invoice</span>
                </button>
              )}

              {canBuildTripSheet && !quote.invoice_id && showBuildTripSheet && (
                <button
                  onClick={() => navigate(`/trip-sheet?quoteid=${quote.id}`)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 @sm:px-5 py-2.5 @sm:py-3 bg-primary-700 text-white text-sm rounded-full transition-colors"
                >
                  <RiFileAddLine className="w-4 h-4" />
                  <span>Build Trip Sheet</span>
                </button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuoteCard;